import type {ConsentDocumentDtoV2} from '@getmoss/api-gateway'
import {useRouter} from 'next/router'
import {useEffect, useMemo, useRef, useState} from 'react'
import {MKT_CONSENT, PRIVACY_CONSENT} from 'src/common/constants'
import {useMossApi} from 'src/common/hooks/useMossApi'

export type Locale = 'en' | 'de' | 'nl'
const FALLBACK_CONSENT_LANG = 'en'

export const useFunnelConsents = (locale: Locale) => {
  const {mossApi} = useMossApi({})
  const {locale: routerLocale} = useRouter()
  const loc = locale || (routerLocale?.split('-')[0].toLowerCase() as Locale) || 'en'
  const [consents, setConsents] = useState<ConsentDocumentDtoV2[] | null>(null)
  const loaded = useRef(false)
  const consentsLoading = !consents && !loaded.current

  // Get consents from API
  useEffect(() => {
    const getConsents = async () => {
      try {
        const response = await mossApi.consentControllerV2.getGeneralFunnelSuitable({})
        setConsents(response.data)
        loaded.current = true
      } catch (error) {
        console.error('Error fetching consents:', error)
      }
    }

    if (!loaded.current) {
      getConsents()
    }
  }, [mossApi])

  return useMemo(() => {
    if (consentsLoading || !consents) {
      return {dppURL: '', marketingURL: '', dppDocId: '', marketingDocId: ''}
    }

    const dpp = consents.find((consent) => consent.type === 'DPP')
    const marketing = consents.find((consent) => consent.type === 'USER_MARKETING')

    if (dpp?.urls && marketing?.urls) {
      const dppURL = dpp.urls[loc] || dpp.urls[FALLBACK_CONSENT_LANG] || PRIVACY_CONSENT[loc].url

      const marketingURL = marketing.urls[loc] || marketing.urls[FALLBACK_CONSENT_LANG] || MKT_CONSENT[loc].url

      return {dppURL, marketingURL, dppDocId: dpp.id, marketingDocId: marketing.id}
    } else {
      return {
        dppURL: PRIVACY_CONSENT[loc].url,
        marketingURL: MKT_CONSENT[loc].url,
        dppDocId: PRIVACY_CONSENT[loc].id,
        marketingDocId: MKT_CONSENT[loc].id,
      }
    }
  }, [consents, loc, consentsLoading])
}
